/**
 * Composable for admin notification counts (badges on Admin menu and submenus).
 * Fetches and polls every 60s only while setEnabled(true) (i.e. the user is an
 * admin); exposes refresh() for manual updates.
 */
import { ref, computed, onUnmounted } from 'vue';
import { videoApi } from '@/services/api.js';

const POLL_INTERVAL_MS = 60000;

let pollTimer = null;

export function useAdminNotifications() {
  const counts = ref({});
  const totals = ref({ admin: 0, creators: 0 });
  const loading = ref(false);
  const enabled = ref(false);

  const totalAdmin = computed(() => totals.value.admin ?? 0);
  const totalCreators = computed(() => totals.value.creators ?? 0);

  async function fetchNotifications() {
    if (!enabled.value) return;
    loading.value = true;
    try {
      const res = await videoApi.getAdminNotifications();
      const d = res?.data;
      counts.value = d?.data ?? {};
      totals.value = d?.totals ?? { admin: 0, creators: 0 };
    } catch (e) {
      console.error('Failed to fetch admin notifications:', e);
      counts.value = {};
      totals.value = { admin: 0, creators: 0 };
    } finally {
      loading.value = false;
    }
  }

  function refresh() {
    fetchNotifications();
  }

  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    }, POLL_INTERVAL_MS);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  // Non-admins polling this endpoint produced ~60k 401/403 error reports.
  function setEnabled(value) {
    if (enabled.value === Boolean(value)) return;
    enabled.value = Boolean(value);
    if (enabled.value) {
      fetchNotifications();
      startPolling();
    } else {
      stopPolling();
      counts.value = {};
      totals.value = { admin: 0, creators: 0 };
    }
  }

  onUnmounted(() => {
    stopPolling();
  });

  return {
    counts,
    totals,
    totalAdmin,
    totalCreators,
    loading,
    refresh,
    setEnabled,
  };
}
